import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useMutation } from '@apollo/client';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';

import {
  Tag,
  Toggle,
} from '@playbookux/playbook-web-core';
import {
  Container,
  Search,
  ContainerTitle,
  Title,
  ContainerTags,
  ContainerTag,
  Link,
  Scroll,
  ScrollContainer,
  ScrollContent,
  Reactions,
  BackgroundIcon,
  ReactionIcon,
  ContainerIcons,
  Icon,
  ContainerMessage,
  Message,
  Loader,
  ContainerLoader,
} from './styles';

import CustomAccordion from '../../../core/components/expand-row';
import AccordionTitle from './accordion-title';
import useTags from '../../../core/hooks/use-tags';
import { CREATE_TAG, CREATE_TAG_HIGHLIGHT } from '../../../core/graphql/tags';
import {
  updateAnswersTagHighlightsCreated,
  updateSpeakersTagHighlightsCreated,
} from '../../../core/graphql/test-sessions';
import {
  updateNoteTagHighlightsCreated,
} from '../../../core/components/video-tab/graphql';
import ModalOnHighlight from '../../../core/components/modal-on-highlight';
import TagHighlightEntityColumn from '../../../core/constants/tag-highlight-entity-column';

const TagModule = (props) => {
  const {
    companyId,
    match: {
      params: {
        projectId,
      },
    },
    testSessionId,
  } = props;

  const {
    reactions,
    groupedTags,
    loading,
    refetch,
  } = useTags(companyId);

  const [createTag] = useMutation(CREATE_TAG);
  const [createTagHighlight] = useMutation(CREATE_TAG_HIGHLIGHT);

  const [isOpen, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [targetScroll, setTargetScroll] = useState();
  const [selectionInfo, setSelectionInfo] = useState(null);
  const tagsRef = useRef({});

  const handleClickToggle = useCallback((id) => {
    setSelectedTags({
      ...selectedTags,
      [id]: !selectedTags[id],
    });
  }, [selectedTags, setSelectedTags]);

  const handleExpandGroup = (id) => {
    setExpandedGroups({
      ...expandedGroups,
      [id]: !expandedGroups[id],
    });
  };

  const handleSearchChange = (e, { value }) => {
    setSearch(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      createTag({
        variables: {
          companyId,
          name: search,
        },
      })
        .then(({ data }) => {
          const { tag } = data.createTag;
          refetch();
          setSelectedTags({
            ...selectedTags,
            [tag.id]: true,
          });
          setTargetScroll(tag.id);
        });
      setSearch('');
    }
  };

  const handleModalHighlightOpen = (sel) => {
    setSelectionInfo(sel);
    if (sel) {
      setOpen(true);
    }
  };

  const handleModalHighlightClose = useCallback(() => {
    setOpen(false);

    if (selectionInfo) {
      const baseAttributes = {
        entityId: selectionInfo.id,
        entityType: selectionInfo.entity,
        entityColumn: TagHighlightEntityColumn[selectionInfo.entity],
        projectId,
        companyId,
        charStart: selectionInfo.charStart,
        charEnd: selectionInfo.charEnd,
      };
      const tagHighlights = Object.keys(selectedTags).map((stId) => {
        if (selectedTags[stId]) {
          return {
            ...baseAttributes,
            tagId: stId,
          };
        }
        return null;
      }).filter((th) => !!th);
      if (tagHighlights.length) {
        switch (selectionInfo.entity) {
          case 'TestSessionAnswer':
            createTagHighlight({
              variables: {
                tagHighlights,
              },
              update: updateAnswersTagHighlightsCreated(
                testSessionId,
                selectionInfo.id,
              ),
            });
            break;
          case 'TestSessionNote':
            createTagHighlight({
              variables: {
                tagHighlights,
              },
              update: updateNoteTagHighlightsCreated(
                selectionInfo.id,
              ),
            });
            break;
          case 'TestSessionSpeaker':
            createTagHighlight({
              variables: {
                tagHighlights,
              },
              update: updateSpeakersTagHighlightsCreated(
                testSessionId,
                selectionInfo.id,
              ),
            });
            break;
          default:
            break;
        }
      }
    }
    setSelectedTags({});
  }, [createTagHighlight, companyId, projectId, selectedTags, selectionInfo, testSessionId]);

  useEffect(() => {
    if (targetScroll) {
      setTimeout(() => {
        const ref = tagsRef.current[targetScroll];
        ref.scrollIntoView();
        setTargetScroll(null);
      }, 300);
    }
  }, [targetScroll]);

  useEffect(() => {
    setExpandedGroups(_.keyBy(groupedTags, 'id'));
  }, [groupedTags]);

  const filteredTags = useMemo(() => {
    const newGroups = [];
    const searchQuery = search.toLowerCase();

    if (searchQuery === '') {
      return groupedTags;
    }

    groupedTags.forEach((tagGroup) => {
      if (tagGroup.name.toLowerCase().includes(searchQuery)) {
        newGroups.push(tagGroup);
      } else {
        const filteredGroup = {
          ...tagGroup,
          tags: tagGroup.tags.filter((tag) => (
            tag.name.toLowerCase().includes(searchQuery)
          )),
        };
        if (filteredGroup.tags.length > 0) {
          newGroups.push(filteredGroup);
        }
      }
    });

    return newGroups;
  }, [groupedTags, search]);

  const showTags = !_.isEmpty(_.filter(groupedTags, (tagGroup) => !_.isEmpty(tagGroup.tags)));

  return (
    <ModalOnHighlight
      isOpen={isOpen}
      onTextSelected={handleModalHighlightOpen}
      onClickOutside={handleModalHighlightClose}
    >
      <Container>
        <Search
          placeholder="Search or create new tag"
          onSearchChange={handleSearchChange}
          value={search}
          showNoResults={false}
          onKeyDown={handleKeyDown}
        />
        <ContainerTitle>
          <Title>TAGS</Title>
          <Link href="/manage-tags" target="_blank">
            Manage tags
            {' '}
            <Icon name="external alternate" />
          </Link>
        </ContainerTitle>

        {loading && (
          <ContainerLoader>
            <Loader data-loader="tag-module-loader" />
          </ContainerLoader>
        )}

        <ContainerTags>
          {!showTags && (
            <ContainerMessage>
              <Message>You don&apos;t have any tags set up.</Message>
              <Message>Click Manage tags to get started.</Message>
            </ContainerMessage>
          )}

          {!loading && showTags && (
            <ScrollContainer>
              <Scroll borderless>
                <ScrollContent>
                  {filteredTags.map((tagGroup, index) => (
                    <CustomAccordion
                      key={tagGroup.id}
                      index={index}
                      expanded={!!expandedGroups[tagGroup.id]}
                      onClick={() => handleExpandGroup(tagGroup.id)}
                    >
                      <CustomAccordion.RowTitle>
                        <AccordionTitle
                          index={index}
                          title={tagGroup.name}
                        />
                      </CustomAccordion.RowTitle>
                      <CustomAccordion.RowContent>
                        {tagGroup.tags.map((tag) => (
                          <ContainerTag
                            key={tag.id}
                            ref={((el) => { tagsRef.current[tag.id] = el; })}
                          >
                            <Tag
                              text={tag.name}
                              color={tagGroup.color}
                            />
                            <Toggle
                              onChange={() => handleClickToggle(tag.id)}
                              checked={!!selectedTags[tag.id]}
                              id={tag.id}
                            />
                          </ContainerTag>
                        ))}

                      </CustomAccordion.RowContent>
                    </CustomAccordion>
                  ))}
                </ScrollContent>
              </Scroll>
            </ScrollContainer>
          )}

        </ContainerTags>
        <ContainerTitle>
          <Title>REACTIONS</Title>
        </ContainerTitle>

        <Reactions>
          {reactions.map((reaction) => (
            <ContainerIcons
              key={reaction.key}
            >
              <BackgroundIcon
                active={selectedTags[reaction.id]}
                backgroundColor={reaction.backgroundColor}
                onClick={() => handleClickToggle(reaction.id)}
              >
                <ReactionIcon src={reaction.icon} />
              </BackgroundIcon>
            </ContainerIcons>
          ))}
        </Reactions>

      </Container>
    </ModalOnHighlight>
  );
};

TagModule.propTypes = {
  companyId: PropTypes.string.isRequired,
  testSessionId: PropTypes.string,
  match: PropTypes.shape({
    params: PropTypes.shape({
      projectId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

TagModule.defaultProps = {
  testSessionId: '',
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

export default compose(withRouter, connect(mapStateToProps))(TagModule);
